'use client';

/**
 * Dependency imports
 */
import React from 'react';
import { Container, Stack, StackProps, Text, Title } from '@mantine/core';

/**
 * Local imports
 */
import HighlightedText from './HighlightedText';
import { useResponsive } from '@/hooks/use-responsive';

/**
 * PageTitle Component
 */
export interface PageTitleProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  tagline?: React.ReactNode;
}

export type PageTitleCombinedProps = PageTitleProps &
  Omit<StackProps, keyof PageTitleProps>;

const PageTitle: React.FC<PageTitleCombinedProps> = ({
  title,
  subtitle,
  tagline,
  ...props
}) => {
  const { responsive } = useResponsive();

  return (
    <Stack {...props}>
      <Container
        size="lg"
        display="flex"
        h="100%"
        style={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <HighlightedText
          fz={responsive({
            xs: 16,
            sm: 18,
            md: 21,
          })}
        >
          {subtitle}
        </HighlightedText>

        {typeof title === 'string' ? (
          <Title
            size={responsive({
              xs: 36,
              sm: 48,
              md: 72,
            })}
            fw={700}
          >
            {title}
          </Title>
        ) : (
          title
        )}

        {typeof tagline === 'string' ? (
          <Text
            fz={responsive({
              xs: 18,
              sm: 21,
              md: 24,
            })}
            fw={300}
          >
            {tagline}
          </Text>
        ) : (
          tagline
        )}
      </Container>
    </Stack>
  );
};

export default PageTitle;
